import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostBinding,
    Inject,
    Input,
    OnChanges,
    Optional,
    Output,
    TemplateRef,
} from '@angular/core';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { GRID_CONFIG, GridConfig } from '@frontend/sports/grid/core/feature/config';
import { Column, Group, GroupBadge } from '@frontend/sports/grid/core/feature/model';
import { GridTrackingService, MarketSwitchSelector } from '@frontend/sports/grid/core/feature/tracking';
import { findIndex } from 'lodash-es';

import { ArrowType, DropdownOption, OptionBadge, OptionType } from '../../dropdown/models';

@Component({
    selector: 'ms-group-selector',
    templateUrl: './group-selector.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupSelectorComponent implements OnChanges {
    @Input() column: Column;
    @Input() columnCount: number;
    @Input() contentClass: string;
    @Input() offerBadgeTemplate?: TemplateRef<any>;
    @Output() groupChange = new EventEmitter<Group>();
    @Output() moreChange = new EventEmitter<boolean>();
    @HostBinding('class.group-selector') classGroupSelector = true;
    @Input() tracking: string;
    @Input() popperAppendTo: string;
    @Input() positionEvent?: string;

    activeOption?: DropdownOption<Group>;
    inExtendedGroups = false;
    hasMarketSwitcherGroupTitle: boolean;
    private hasExtendedGroups: boolean;
    private extendedGroupOptions: DropdownOption<Group>[];
    private groupOptions: DropdownOption<Group>[];

    get options(): DropdownOption<Group>[] {
        return this.inExtendedGroups ? this.extendedGroupOptions : this.groupOptions;
    }

    constructor(
        @Optional() @Inject(GRID_CONFIG) private gridConfig: GridConfig | null,
        private gridTrackingService: GridTrackingService,
        public sitecore: Sitecore,
    ) {
        this.hasMarketSwitcherGroupTitle = !!this.gridConfig?.hasMarketSwitcherGroupTitle;
    }

    ngOnChanges(): void {
        this.hasExtendedGroups = this.column.groups.some((group) => group.extended);
        this.groupOptions = this.mapOptions(false);
        this.extendedGroupOptions = this.mapOptions(true);

        const activeGroup = this.column.groups.find((group) => group.active);

        if (activeGroup) {
            if (this.activeOption?.payload?.id !== activeGroup.id) {
                this.inExtendedGroups = activeGroup.extended;
            }

            this.activeOption = this.mapOption(activeGroup);
        } else {
            throw new Error('Column should have active group');
        }
    }

    selected(option: DropdownOption): void {
        switch (option.type) {
            case OptionType.Regular:
                this.groupSelected(option);

                return;
            case OptionType.Special:
                this.groupTypeSwitch();

                return;
        }
    }

    private groupSelected({ payload }: DropdownOption): void {
        const group = payload as Group;

        this.gridTrackingService.trackMarketSwitch({
            trackingLocation: this.tracking,
            marketName: group.name,
            marketId: group.id,
            marketPosition: findIndex(this.options, (option) => option.id === group.id),
            marketSwitchSelector: MarketSwitchSelector.Dropdown,
            pageReferringAction: group.extended ? 'MarketSwitcher_MoreMarkets' : `MarketSwitcher_${group.id}`,
            positionEvent: this.positionEvent,
        });

        this.groupChange.next(group);
    }

    private groupTypeSwitch(): void {
        this.inExtendedGroups = !this.inExtendedGroups;

        if (!this.hasExtendedGroups) {
            this.moreChange.next(this.inExtendedGroups);
        }
    }

    private mapOptions(extended: boolean): DropdownOption<Group>[] {
        let options = this.column.groups
            .filter((group) => group.extended === extended && group.visible && !group.isFallbackGroup)
            .map((group) => this.mapOption(group));

        if (options.length > 0 && this.column.more) {
            options = [
                ...options,
                {
                    id: extended ? 'back' : 'more',
                    name: extended ? this.sitecore.eventGrid.MarketSwitcher_Back : this.sitecore.eventGrid.MarketSwitcher_More,
                    type: OptionType.Special,
                    arrowType: extended ? ArrowType.Left : ArrowType.Right,
                    automation: extended ? 'group-selector-back' : 'group-selector-more',
                },
            ];
        }

        return options;
    }

    private mapOption(group: Group): DropdownOption<Group> {
        return {
            id: group.id,
            name: group.name,
            payload: group,
            type: OptionType.Regular,
            arrowType: ArrowType.None,
            automation: { groupId: group.id },
            badge: group.badge ? this.mapBadge(group.badge) : undefined,
        };
    }

    private mapBadge(group: GroupBadge): OptionBadge {
        return {
            text: group.text,
            cssClass: group.cssClass,
        };
    }
}
